import { UserFeature, UserFeatureList } from 'src/models/user.models'

/**
 * Reads the `PHYSICAL_CARD_TRANSACTIONS_FEED` entitlement from `/features`,
 * distinguishing **absent** from **explicitly false** — the distinction the
 * whole gate turns on.
 *
 * `/features` emits this key only when an org-scoped SaaS verdict applies.
 * `GetFeaturesCommand` (extend-api) returns the plan entitlement map when
 * `isSubjectToSaasLimits` is true; every other org gets a map with no such key.
 * Absence has a second producer too: `GetFeaturesCommand.execute` early-returns
 * the user-feature map when `grant.user.organization?.id` is null, before
 * `getSaasEntitlements` runs at all, and `PHYSICAL_CARD_TRANSACTIONS_FEED` is a
 * `Feature` but not a `UserFeature`, so an org-less caller never sees it either.
 * So the three cases are:
 *
 * | value    | meaning                                               | entitled |
 * |----------|-------------------------------------------------------|----------|
 * | absent   | no org-scoped SaaS verdict applies — a non-SaaS org,   | yes      |
 * |          | where the issuer/BIN already decided eligibility at    |          |
 * |          | enrollment, or a caller with no organization at all    |          |
 * | `false`  | SaaS org whose plan lacks the entitlement              | no       |
 * | `true`   | SaaS org that holds the entitlement                    | yes      |
 *
 * Reading absence as "not entitled" is the bug this exists to prevent: it
 * misclassifies every non-SaaS org (Astrada/Sunflower and friends), who enroll
 * physical cards perfectly well because `resolveEnrollmentEligibility` refuses
 * to let this feature downgrade them. It also fails closed while the query is
 * in flight, flashing the surface absent on first paint.
 *
 * Callers that surface a *connect* action gate on this; the SaaS org that
 * genuinely lacks the entitlement is the only population it withholds from.
 * Note this says nothing about whether the org is SaaS — request
 * {@link UserFeature.SAAS_ENABLED} alongside if you need that explicitly.
 *
 * Because an unresolved query reads as entitled, a caller that gates an
 * *action* rather than mere visibility should AND this with the query having
 * resolved (`isSuccess`), so it never offers an enrollment the backend would
 * reject. `AccountSettings` does exactly that for its Connect CTA.
 *
 * @param userFeatures - the `/features` response map, or undefined while the
 *   query is pending, errored, or disabled
 * @returns true unless the backend explicitly reported the feature off
 */
export const isPhysicalCardTransactionsFeedEntitled = (
  userFeatures: UserFeatureList | undefined,
): boolean =>
  userFeatures?.[UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED] !== false
